import {
    createContext,
    memo,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useAuth } from 'react-oidc-context';

import type {
    IEnhancedGeoLocationInterface,
    ILocationData,
} from '@endeavour/enhanced-geo-integration';
import { GeoLocationStatus } from '@endeavour/enhanced-geo-integration';
import { GeoLocationError } from '@endeavour/enhanced-geo-integration/src/interface/errors.interface';

import { config } from '../../../config/config';
import { isInApp } from '../../main/util/is-app';
import styles from '../../sessions/components/compliance-prompt/compliance-prompt.module.scss';
import { FormattedMessage } from '../../shared/components/formatted-message/formatted-message';
import { Icon } from '../../shared/components/icon/icon';
import { ModalContainer } from '../../shared/components/modal-container/modal-container';
import { createStyleHelper } from '../../shared/utils/class-names';
import type { FCWithChildren } from '../../shared/utils/component.interface';
import { useIsEnhancedGeolocationProviderEnabled } from '../hooks/use-geolocation-enabled';

const style = createStyleHelper(styles, 'compliance-prompt');
interface IGeolocationContext {
    integration: IEnhancedGeoLocationInterface | null;
    status: ILocationData;
}

const GeoLocationContext = createContext<IGeolocationContext>({
    integration: null,
    /** Default: No integration = Allowed */
    status: {
        status: GeoLocationStatus.ALLOWED,
        errors: [],
        token: undefined,
    },
});

export const GeolocationContextProvider: FCWithChildren<{
    integration: IEnhancedGeoLocationInterface;
}> = memo(({ children, integration }) => {
    const { user, isAuthenticated, isLoading } = useAuth();
    const isEnabled = useIsEnhancedGeolocationProviderEnabled();
    const profileId = user?.profile.user_profile_id as string | undefined;
    const [isRunning, setIsRunning] = useState(true);
    const isNativeApp = isInApp();
    const auth = useAuth();
    const isLoggedIn = auth.isAuthenticated && auth.user;
    const urlParams = new URLSearchParams(window.location.search);
    const sourceParam = urlParams.get('source');
    const [currentUrl, setCurrentUrl] = useState('');
    const [latestStatus, setLatestStatus] = useState<ILocationData>({
        status:
            !integration || (!isAuthenticated && !isLoading)
                ? GeoLocationStatus.ALLOWED
                : GeoLocationStatus.PENDING,
        errors: [],
        token: undefined,
    });
    const context = useMemo(
        () => ({
            integration,
            status: latestStatus,
        }),
        [integration, latestStatus],
    );

    const timerRef = useRef<number>();
    // Using a ref to avoid triggering the effect every time the status changes
    const statusRef = useRef(latestStatus.status);

    const handleStatusChange = useCallback((newStatus: ILocationData) => {
        clearTimeout(timerRef.current);

        if (
            statusRef.current === GeoLocationStatus.ALLOWED &&
            newStatus.status === GeoLocationStatus.PENDING
        ) {
            timerRef.current = setTimeout(() => {
                statusRef.current = newStatus.status;
                setLatestStatus(newStatus);
            }, config.GEOLOCATION.PENDING_GRACE_PERIOD);
            return;
        }

        statusRef.current = newStatus.status;
        setLatestStatus(newStatus);
    }, []);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    // Url check
    useEffect(() => {
        // Extract the current URL when the component mounts
        setCurrentUrl(window.location.pathname);
    }, [window.location.pathname]);

    const handleError = () => {
        handleStatusChange({
            status: GeoLocationStatus.DENIED,
            errors: [GeoLocationError.UNKNOWN_ERROR],
            token: undefined,
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!integration) {
                return;
            }

            try {
                const isRunning = await integration?.isVerifyRunning().catch(handleError);
                if (isRunning === true || isRunning === false) {
                    setIsRunning(isRunning);
                }
            } catch (error) {
                console.log(error);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (!integration) {
            return;
        }

        if ((!isAuthenticated && !isLoading) || !isEnabled) {
            // Allow both demo mode and disabled state without geolocation
            handleStatusChange({ status: GeoLocationStatus.ALLOWED, errors: [], token: undefined });
        }

        if (isAuthenticated && profileId && !isLoading && isEnabled) {
            const handleError = () => {
                handleStatusChange({
                    status: GeoLocationStatus.DENIED,
                    errors: [GeoLocationError.UNKNOWN_ERROR],
                    token: undefined,
                });
            };

            const handleStatus = (status: ILocationData | void) => {
                if (hasLoadedFirstValue || !status) {
                    return;
                }

                handleStatusChange(status);
            };

            let hasLoadedFirstValue = false;
            integration
                .startLocationCheck({
                    userProfileId: profileId,
                })
                .then(handleStatus)
                .catch(handleError);
            // if (isNativeApp) {
            //     console.log('Post message being sent');
            //     window.parent.postMessage(
            //         { message: 'external_geolocation_iframe_ready' },
            //         'https://crabmd-staging.sportnco.com/',
            //     );
            // }
            // integration?.getDetectedLocationInformation().then(handleStatus).catch(handleError);

            const unsubscribe = integration?.onLocationRefreshed((event) => {
                hasLoadedFirstValue = true;
                handleStatusChange(event);
            });

            return () => {
                integration.endLocationCheck().catch(handleError);
                unsubscribe();

                // Ensure we can't store stale data
                hasLoadedFirstValue = true;
            };
        }
    }, [profileId, handleStatusChange, isAuthenticated, isEnabled, isLoading, integration]);
    const onClose = () => {
        setIsRunning(true);
    };

    return (
        <GeoLocationContext.Provider value={context}>
            {children}
            {isNativeApp || sourceParam === 'nativeapp' ? (
                <></>
            ) : isRunning ? (
                <></>
            ) : !isLoggedIn ? (
                <></>
            ) : !currentUrl.includes('sports') ? (
                <></>
            ) : (
                <ModalContainer onClose={onClose}>
                    <section className={style()}>
                        <Icon name={'alert-triangle'} />
                        <h1 className={style('title')}>
                            <FormattedMessage
                                id={'xpoint.isrunning.block.title'}
                                defaultMessage={'Not permitted'}
                            />
                        </h1>
                        <p className={style('description')}>
                            <a
                                href={'https://install.xpoint.tech/'}
                                className={style('description')}
                                rel="noreferrer"
                            >
                                <FormattedMessage
                                    id="xpoint.isrunning.link"
                                    defaultMessage="https://install.xpoint.tech/"
                                />
                            </a>
                            <FormattedMessage
                                id="xpoint.isrunning.description"
                                defaultMessage=" Install and launch the xpoint-verify application, then please reload this page "
                            />
                        </p>
                    </section>
                </ModalContainer>
            )}
        </GeoLocationContext.Provider>
    );
});

export function useGeolocationIntegration() {
    const context = useContext(GeoLocationContext);

    return context.integration;
}

export function useGeolocationStatus() {
    const context = useContext(GeoLocationContext);

    return context.status;
}
